import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Menu, MenuItem } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { IoHome, IoSparkles, IoWarning, IoShieldCheckmark, IoSearch, IoChevronDown, IoVideocam, IoFlame, IoDocumentText, IoPersonOutline, IoCube, IoCalendar } from "react-icons/io5";

function PageNavbar() {
  const [servicesMenu, setServicesMenu] = useState(null);
  const location = useLocation();

  const handleOpenServicesMenu = (event) => {
    setServicesMenu(event.currentTarget);
  };

  const handleCloseServicesMenu = () => {
    setServicesMenu(null);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const services = [
    { name: "Dubai AI", path: "/dubai-ai", icon: <IoSparkles size="18px" /> },
    { name: "Restricted Zone Monitor", path: "/restricted-zone-monitor", icon: <IoWarning size="18px" /> },
    { name: "PPE Detection Dashboard", path: "/ppe-detection-dashboard", icon: <IoShieldCheckmark size="18px" /> },
    { name: "Lost and Found", path: "/lost-and-found", icon: <IoSearch size="18px" /> },
    { name: "Theft Detection", path: "/theft-detection", icon: <IoVideocam size="18px" /> },
    { name: "Fire Detection", path: "/fire-detection", icon: <IoFlame size="18px" /> },
    { name: "Document Archiving", path: "/document-archiving", icon: <IoDocumentText size="18px" /> },
    { name: "Fall Detection", path: "/fall-detection", icon: <IoPersonOutline size="18px" /> },
    { name: "AI Inventory Agent", path: "/ai-inventory-agent", icon: <IoCube size="18px" /> },
    { name: "Attendance", path: "/attendance", icon: <IoCalendar size="18px" /> }
  ];

  return (
    <AppBar
      position="relative"
      sx={{
        backdropFilter: "blur(42px)",
        backgroundColor: "transparent",
        backgroundImage: "linear-gradient(123.64deg, rgba(255, 255, 255, 0) -22.38%, rgba(255, 255, 255, 0.039) 70.38%)",
        boxShadow: "0px 20px 27px 0px rgba(0, 0, 0, 0.05)",
        borderRadius: "20px",
        marginBottom: "24px",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 24px !important",
        }}
      >
        {/* Logo */}
        <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
          <VuiBox display="flex" flexDirection="column" alignItems="flex-start" sx={{ cursor: "pointer" }}>
            <VuiTypography
              variant="h4"
              color="white"
              fontWeight="bold"
              sx={{
                fontSize: "18px",
                letterSpacing: "0.2em",
                lineHeight: "1",
                marginBottom: "2px"
              }}
            >
              ZAINLEE
            </VuiTypography>
            <VuiTypography
              variant="caption"
              color="text"
              sx={{
                fontSize: "10px",
                letterSpacing: "0.3em",
                opacity: 0.7
              }}
            >
              TECHNOLOGIES
            </VuiTypography>
          </VuiBox>
        </Link>

        {/* Navigation Buttons */}
        <VuiBox display="flex" alignItems="center" gap={2}>
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <VuiButton
              variant="contained"
              color="info"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                borderRadius: "12px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: "700",
                boxShadow: isActive("/dashboard") ? "0px 4px 15px rgba(33, 150, 243, 0.4)" : "none",
                "&:hover": { boxShadow: "0px 6px 20px rgba(33, 150, 243, 0.5)" },
              }}
            >
              <IoHome size="18px" />
              Home
            </VuiButton>
          </Link>

          <VuiButton
            variant="contained"
            color="info"
            onClick={handleOpenServicesMenu}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 20px",
              borderRadius: "12px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: "700",
              boxShadow: services.some(s => isActive(s.path)) ? "0px 4px 15px rgba(33, 150, 243, 0.4)" : "none",
              "&:hover": { boxShadow: "0px 6px 20px rgba(33, 150, 243, 0.5)" },
            }}
          >
            Services
            <IoChevronDown size="16px" />
          </VuiButton>

          <Menu
            anchorEl={servicesMenu}
            open={Boolean(servicesMenu)}
            onClose={handleCloseServicesMenu}
            sx={{
              "& .MuiPaper-root": {
                background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
                backdropFilter: "blur(42px)",
                borderRadius: "12px",
                boxShadow: "0px 20px 27px 0px rgba(0, 0, 0, 0.05)",
                marginTop: "8px",
                minWidth: "250px",
              },
            }}
          >
            {services.map((service) => (
              <MenuItem
                key={service.path}
                component={Link}
                to={service.path}
                onClick={handleCloseServicesMenu}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 20px",
                  color: isActive(service.path) ? "#ffffff" : "#a0aec0",
                  background: isActive(service.path) ? "rgba(67, 24, 255, 0.1)" : "transparent",
                  borderRadius: "8px",
                  margin: "4px 8px",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                  },
                }}
              >
                {service.icon}
                <VuiTypography
                  variant="button"
                  color="inherit"
                  fontWeight={isActive(service.path) ? "bold" : "regular"}
                  sx={{ fontSize: "14px", textTransform: "none" }}
                >
                  {service.name}
                </VuiTypography>
              </MenuItem>
            ))}
          </Menu>
        </VuiBox>
      </Toolbar>
    </AppBar>
  );
}

export default PageNavbar;

